import React, { useState } from "react";
import {
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  useDownloadMonthlyReport,
  useGenerateMonthlyReport,
  useMonthlyReports,
} from "../hooks/useMonthlyReports";
import { MonthlyReportMeta } from "../services/api";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// System launch — no payment data exists before September 2025.
const FIRST_REPORT_YEAR = 2025;

const previousMonth = (): { year: number; month: number } => {
  const now = new Date();
  return now.getMonth() === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month: now.getMonth() };
};

const MonthlyReports: React.FC = () => {
  const initial = previousMonth();
  const [year, setYear] = useState<number>(initial.year);
  const [month, setMonth] = useState<number>(initial.month);

  const { data: reports = [], isLoading } = useMonthlyReports();
  const generateReport = useGenerateMonthlyReport();
  const downloadReport = useDownloadMonthlyReport();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const years: number[] = [];
  for (let y = currentYear; y >= FIRST_REPORT_YEAR; y--) {
    years.push(y);
  }
  const selectableMonths = MONTH_NAMES.map((name, index) => ({
    name,
    value: index + 1,
  })).filter((m) => year < currentYear || m.value <= currentMonth);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);

  const formatDateTime = (date: string) =>
    new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const monthLabel = (report: MonthlyReportMeta) =>
    `${MONTH_NAMES[report.month - 1]} ${report.year}`;

  const selectClasses =
    "rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Monthly Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Inter-company report PDF: dealer activation fees and top-ups per
            month (net, by payment date)
          </p>
        </div>
      </div>

      {/* Generate card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className={selectClasses}
            >
              {selectableMonths.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Year
            </label>
            <select
              value={year}
              onChange={(e) => {
                const nextYear = Number(e.target.value);
                setYear(nextYear);
                // Keep the month selectable when switching into the current year.
                if (nextYear === currentYear && month > currentMonth) {
                  setMonth(currentMonth);
                }
              }}
              className={selectClasses}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => generateReport.mutate({ year, month })}
            disabled={generateReport.isPending}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentChartBarIcon className="h-4 w-4 mr-2" />
            {generateReport.isPending ? "Generating..." : "Generate Report"}
          </button>
        </div>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          The PDF downloads automatically after generation. Regenerating an
          already-generated month replaces the stored file and produces
          identical figures for closed months. Cashback transactions are not
          part of this report.
        </p>
      </div>

      {/* Previously generated reports */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : reports.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Activation Fees
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Top-ups
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Net
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vouchers Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Generated
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {reports.map((report) => (
                  <tr
                    key={report._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {monthLabel(report)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {report.fileName}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {report.activationFeeCount} ·{" "}
                      {formatCurrency(report.activationFeeNet)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {report.topUpCount} · {formatCurrency(report.topUpNet)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(report.totalNet)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {report.voucherActivationCount ?? "–"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDateTime(report.generatedAt)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {report.generatedBy
                          ? `by ${report.generatedBy}`
                          : ""}
                        {report.generationCount > 1
                          ? ` · ${report.generationCount}x generated`
                          : ""}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => downloadReport.mutate(report._id)}
                        disabled={downloadReport.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <DocumentChartBarIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No reports generated yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Pick a month above and generate the first report
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyReports;
